import { handleMarkDevicePatientReturned } from '@/entities/device';
import { useRmaStore } from '@/entities/rma';
import { useUserStore } from '@/entities/user';
import { useSetState } from '@/shared/lib/customHooks';
import {
  convertSortOrder,
  renderSortIcon,
  showMessageError,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import _ from 'lodash';
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { SEARCH_BY_LIST } from '../../../config/waitingToReturnTab';
import {
  actionFormatter,
  deviceIdFormatter,
  facilityFormatter,
  getReturnDate,
  patientNameFormatter,
  shippedDateFormatter,
  studyFidFormatter,
  studyStartDateFormatter,
  trackingFormatter,
} from '../../../lib/waitingToReturnTable';

/**
 * Backs the waiting-to-return RMA table: it holds the drawer, modal, sort and row-selection state and derives the query filter sent to the table. Memoizes that filter from sort order, the stored search value and search-by option, the selected facility and the all-studies scope, and returns the device, study and patient drawer togglers, the mark-as-returned modal flow, the column builder, the paging cursor and a rowSelection object that unions or removes toggled rows by id.
 *
 * @returns The search value, table ref, state, filter, togglers, column builder, paging cursor and selection helpers.
 */
const useWaitingToReturnTable = () => {
  const navigate = useNavigate();

  /**
   * The persisted waiting-to-return search filter held in the RMA store. Selected off useRmaStore and destructured for the searchValue and searchByValue that feed the table filter.
   *
   * @returns The stored waiting-to-return filter object.
   */
  const waitingToReturnFilter = useRmaStore((s) => s.waitingToReturnFilter);
  const { searchValue = '', searchByValue = SEARCH_BY_LIST[0].value } = waitingToReturnFilter;

  /**
   * The facility currently chosen by the user, read from the user store. Selected off useUserStore as selectedFacility and used to scope the table filter to that facility.
   *
   * @returns The selected facility option.
   */
  const selectedFacility = useUserStore((s) => s.selectedFacility);
  /**
   * The patient-scope option currently chosen by the user, read from the user store. Selected off useUserStore as selectedTypeSeePatients and compared with GETTEXT_ALL_STUDIES to set the filter's viewAll flag.
   *
   * @returns The selected patient-scope option.
   */
  const selectedTypeSeePatients = useUserStore((s) => s.selectedTypeSeePatients);

  const refTableComponent = useRef<any>(undefined);

  const [state, setState] = useSetState<any>({
    // State for Device Info Drawer
    isOpenDeviceInfoDrawer: false,
    // this is device ID
    dataDeviceInfoDrawer: '',

    // State for Study Info Drawer
    isOpenStudyDrawer: false,
    dataStudyDrawer: {},

    // State for Patient Info Drawer
    isOpenPatientDrawer: false,
    // This is study id
    dataPatientDrawer: '',

    // State for mark device returned modal
    isOpenMarkDeviceReturnedModal: false,
    markDeviceReturnedModalData: [],

    // State for table
    sortOrder: 'asc',
    sortBy: 'studyEndDate',
    selectedRow: [],
  });

  /**
   * The query filter sent to the waiting-to-return table, memoized from sort order, search input and user scope. Carries sortOrder and a viewAll flag derived from selectedTypeSeePatients, adds deviceId or a numeric studyFid depending on the selected search-by option, and restricts facilities when a specific facility is selected.
   *
   * @returns The memoized filter used to fetch waiting-to-return rows.
   */
  const filter = useMemo(() => {
    const filter = {
      sortOrder: state.sortOrder,
      sortBy: state.sortBy,
      viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
    };
    if (searchValue) {
      if (searchByValue === SEARCH_BY_LIST[0].value) {
        _.assign(filter, { deviceId: searchValue });
      } else {
        _.assign(filter, { studyFid: parseInt(searchValue, 10) });
      }
    }

    if (selectedFacility?.value && selectedFacility?.value !== GETTEXT_ALL_FACILITIES) {
      _.assign(filter, { facilities: [selectedFacility?.value] });
    }

    return filter;
  }, [
    state.sortOrder,
    state.sortBy,
    searchValue,
    selectedFacility?.value,
    selectedTypeSeePatients?.value,
  ]);

  /**
   * Opens or closes the device information drawer for a waiting-to-return row. Flips isOpenDeviceInfoDrawer and stores the row's deviceId as the drawer's subject.
   *
   * @param data - Row supplying the deviceId, empty by default on close.
   * @returns Nothing.
   */
  const toggleDeviceInfoDrawer = (data: any = {}) => {
    setState({
      isOpenDeviceInfoDrawer: !state.isOpenDeviceInfoDrawer,
      dataDeviceInfoDrawer: data?.deviceId,
    });
  };

  /**
   * Opens or closes the mark-device-returned confirmation modal. Flips isOpenMarkDeviceReturnedModal and keeps the passed rows as markDeviceReturnedModalData.
   *
   * @param data - Rows staged for the mark-as-returned confirmation.
   * @returns Nothing.
   */
  const toggleMarkDeviceReturnedModal = (data: any) => {
    setState({
      isOpenMarkDeviceReturnedModal: !state.isOpenMarkDeviceReturnedModal,
      markDeviceReturnedModalData: data,
    });
  };

  /**
   * Opens or closes the study information drawer for a waiting-to-return row. Flips isOpenStudyDrawer and stores the row's study id inside dataStudyDrawer.
   *
   * @param data - Row supplying the study id, empty by default on close.
   * @returns Nothing.
   */
  const toggleStudyDrawer = (data: any = {}) => {
    setState({
      isOpenStudyDrawer: !state.isOpenStudyDrawer,
      dataStudyDrawer: {
        id: data?.study?.id,
      },
    });
  };

  /**
   * Opens or closes the patient information drawer for a waiting-to-return row. Flips isOpenPatientDrawer and stores the row's study id as dataPatientDrawer.
   *
   * @param data - Row supplying the study id, empty by default on close.
   * @returns Nothing.
   */
  const togglePatientDrawer = (data: any = {}) => {
    setState({
      isOpenPatientDrawer: !state.isOpenPatientDrawer,
      dataPatientDrawer: data?.study?.id,
    });
  };

  /**
   * Marks the devices staged in the confirmation modal as returned with the given received items and return date. Sends their ids through handleMarkDevicePatientReturned, then closes the modal, drops those rows from the selection and calls removeData on the table ref; on failure it logs the error and shows an error message.
   *
   * @param receivedItems - Items confirmed as received back with the devices.
   * @param returnDate - Date the devices were returned.
   * @returns Resolves once the mutation and state cleanup finish, or the error is shown.
   */
  const onMarkDeviceReturned = async (receivedItems: any, returnDate: any) => {
    const ids = _.map(state.markDeviceReturnedModalData, 'id');
    try {
      const input = {
        receivedItems,
        ids,
        returnDate,
      };
      await handleMarkDevicePatientReturned(input);
      const newSelectedRow = _.differenceBy(
        state.selectedRow,
        state.markDeviceReturnedModalData,
        'id',
      );

      setState({
        isOpenMarkDeviceReturnedModal: false,
        selectedRow: newSelectedRow,
        markDeviceReturnedModalData: [],
      });
      if (_.isFunction(refTableComponent.current?.removeData)) {
        refTableComponent.current.removeData(ids, 'id');
      }
    } catch (error) {
      console.error('Failed to mark device patient returned: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Opens the study detail page for the study attached to a waiting-to-return row. Navigates to the studies route for that study id and passes shouldNavigateToReturnStatusTab in the router state so the page lands on its return-status tab.
   *
   * @param row - Table row whose study is being opened.
   * @returns Nothing.
   */
  const onClickViewDetail = (row: any) => {
    navigate(`/studies/${row?.study?.id}`, { state: { shouldNavigateToReturnStatusTab: true } });
  };

  /**
   * Builds the column definitions for the waiting-to-return device table. Wires each dataIndex to its formatter for device id, tracking number, study id, patient name, study stop date and facility plus a fixed action column, forwards isLoadedData for skeleton rendering along with the drawer and modal togglers and onClickViewDetail, and makes the study stop date column sortable with the direction read off the current filter.
   *
   * @param isLoadedData - Whether row data has loaded, forwarded to every formatter.
   * @returns Column descriptors for the waiting-to-return table.
   */
  const generateColumns = (isLoadedData: any) => {
    const columns = [
      {
        dataIndex: 'deviceId',
        title: `${t`Device ID`}`,
        width: 150,
        render: (...props: [any, any, any]) =>
          deviceIdFormatter(...props, {
            isLoadedData,
            toggleDeviceInfoDrawer,
          }),
      },
      {
        dataIndex: ['lastReturnLabel', 'transaction', 'trackingNumber'],
        title: `${t`Tracking number`}`,
        width: 200,
        render: (...props: [any, any, any]) =>
          trackingFormatter(...props, {
            isLoadedData,
          }),
      },
      {
        dataIndex: ['study', 'friendlyId'],
        title: `${t`Study ID`}`,
        width: 120,
        render: (...props: [any, any, any]) =>
          studyFidFormatter(...props, {
            isLoadedData,
            toggleStudyDrawer,
          }),
      },
      {
        dataIndex: ['study', 'info', 'patient', 'firstName'],
        title: `${t`Patient name`}`,
        width: 225,
        render: (...props: [any, any, any]) =>
          patientNameFormatter(...props, {
            isLoadedData,
            togglePatientDrawer,
          }),
      },
      {
        dataIndex: 'studyStartDate',
        title: `${t`Study start date`}`,
        width: 160,
        render: (...props: [any, any, any]) =>
          studyStartDateFormatter(...props, {
            isLoadedData,
          }),
        sorter: true,
        sortOrder: convertSortOrder('studyStartDate', filter),
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      },
      {
        dataIndex: 'studyEndDate',
        title: `${t`Study stop date`}`,
        width: 160,
        render: (...props: [any, any, any]) =>
          shippedDateFormatter(...props, {
            isLoadedData,
          }),
        sorter: true,
        sortOrder: convertSortOrder('studyEndDate', filter),
        sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      },
      {
        dataIndex: ['facility', 'name'],
        title: `${t`Facility`}`,
        width: 356,
        render: (...props: [any, any, any]) =>
          facilityFormatter(...props, {
            isLoadedData,
          }),
      },
      {
        dataIndex: '',
        title: t`Actions`,
        width: 88,
        className: 'action-row',
        fixed: 'right',
        onCell: () => ({ 'aria-label': t`Actions` }),
        render: (...props: [any, any, any]) =>
          actionFormatter(...props, {
            isLoadedData,
            onClickViewDetail,
            toggleMarkDeviceReturnedModal,
          }),
      },
    ];
    return columns;
  };

  /**
   * Clears the device row selection in the waiting-to-return table. Resets state.selectedRow to an empty array.
   *
   * @returns Nothing.
   */
  const onClickRemoveAllSelected = () => {
    setState({ selectedRow: [] });
  };

  /**
   * Starts the mark-as-returned flow for the devices currently ticked in the waiting-to-return table. Opens the confirmation modal with a lodash deep clone of state.selectedRow as its payload.
   *
   * @returns Nothing.
   */
  const handleClickMarkAsReturned = () => {
    toggleMarkDeviceReturnedModal(_.cloneDeep(state.selectedRow));
  };

  /**
   * Applies a new sort direction to the waiting-to-return device table. Stores sortOrder in local state, which feeds the memoized fetch filter.
   *
   * @param sortOrder - New sort direction taken from the sort event payload.
   * @returns Nothing.
   */
  const onSort = ({ sortOrder, sortBy }: any) => {
    setState({ sortOrder, sortBy });
  };

  /**
   * Builds the keyset-pagination cursor for the waiting-to-return device table from the rows already loaded. Takes the studyEndDate of the last row, or undefined when the collection is empty.
   *
   * @param data - Rows currently loaded in the table.
   * @returns An object whose cursor is the last row's studyEndDate.
   */
  const getPagingCursor = (data: any) => {
    if (!data || !data.length) return {};
    const lastItem = data[data.length - 1];
    if (filter.sortBy === 'studyStartDate') {
      return {
        cursor: lastItem?.studyStartDate,
      };
    }
    return {
      cursor: lastItem?.studyEndDate,
    };
  };

  const rowSelection = {
    selectedRowKeys: _.map(state.selectedRow, (item) => item.id),
    getCheckboxProps: (record: any) => ({
      'aria-label': `${t`Select device`} ${record.deviceId}`,
    }),
    renderCell: (_value: any, record: any, _index: any, originNode: any) => ({
      children: originNode,
      props: { 'aria-label': `${t`Select device`} ${record.deviceId}` },
    }),
    onSelect: (row: any, isSelect: any) => {
      if (isSelect && !_.some(state.selectedRow, { id: row.id })) {
        setState({
          selectedRow: [
            ...state.selectedRow,
            {
              id: row.id,
              deviceId: row.deviceId,
              returnDate: getReturnDate(row),
            },
          ],
        });
        return;
      }

      if (!isSelect && _.some(state.selectedRow, { id: row.id })) {
        const clonedSelectedRow = [...state.selectedRow];
        _.remove(clonedSelectedRow, { id: row.id });
        setState({ selectedRow: clonedSelectedRow });
      }
    },
    onSelectAll: (isSelect: any, selectedRows: any, changeRows: any) => {
      /**
       * The id and deviceId pairs extracted from the rows toggled by a select-all action. Derived by mapping changeRows down to those two fields before they are unioned into or removed from the selection.
       *
       * @returns Minimal row descriptors used to update the selection.
       */
      const mapRows = _.map(changeRows, (row) => ({
        id: row.id,
        deviceId: row.deviceId,
      }));

      if (isSelect) {
        setState({
          selectedRow: _.unionBy(state.selectedRow, mapRows, 'id'),
        });
        return;
      }

      setState({
        selectedRow: _.differenceBy(state.selectedRow, mapRows, 'id'),
      });
    },
  };

  const isSelectingDevices = state.selectedRow.length > 0;

  return {
    searchValue,
    refTableComponent,
    state,
    filter,
    toggleDeviceInfoDrawer,
    toggleMarkDeviceReturnedModal,
    toggleStudyDrawer,
    togglePatientDrawer,
    onMarkDeviceReturned,
    generateColumns,
    onClickRemoveAllSelected,
    handleClickMarkAsReturned,
    onSort,
    getPagingCursor,
    rowSelection,
    isSelectingDevices,
  };
};

export { useWaitingToReturnTable };
